<template>
    <div ref="root" class="relative inline-block">
        <Float
            placement="bottom-end"
            :offset="4"
            composable
            flip
            :z-index="50"
            enter="transition duration-200 ease-out"
            enter-from="opacity-0 -translate-y-1"
            enter-to="opacity-100 translate-y-0"
            leave="transition duration-150 ease-in"
            leave-from="opacity-100 translate-y-0"
            leave-to="opacity-0 -translate-y-1"
            :show="open"
        >
            <FloatReference>
                <Button color="default" :size="size" dusk="file-sort-button" type="button" @click="open = !open">
                    <span class="text-neutral-500 dark:text-neutral-400">{{
                        lang('fields::fields.file.sort.label')
                    }}</span>
                    <span class="ml-1">{{ activeLabel }}</span>
                    <icon icon="hero/solid/chevron-down" class="w-3 h-3 ml-1"></icon>
                </Button>
            </FloatReference>

            <FloatContent>
                <ui-popover>
                    <ui-popover-section>
                        <ui-popover-segment>
                            <ui-popover-link
                                v-for="option in options"
                                :key="`${option.sort.key}-${option.sort.dir}`"
                                :dusk="`file-sort-${option.sort.key}-${option.sort.dir === 1 ? 'asc' : 'desc'}`"
                                :clickable="true"
                                :selected="isActive(option.sort)"
                                :active="isActive(option.sort)"
                                @click="select(option.sort)"
                            >
                                {{ option.label }}
                            </ui-popover-link>
                        </ui-popover-segment>
                    </ui-popover-section>
                </ui-popover>
            </FloatContent>
        </Float>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { lang } from '@/lang/composable';
import { onClickOutside } from '@vueuse/core';
import { Float, FloatContent, FloatReference } from '@headlessui-float/vue';
import type { FileSort, FileSortLabels } from '@src/Fields/Pages/Files/fileSorting';

defineOptions({ name: 'FileSortDropdown' });

const props = withDefaults(
    defineProps<{
        labels: FileSortLabels;
        modelValue?: FileSort | null;
        size?: string;
    }>(),
    {
        modelValue: null,
        size: 'sm',
    },
);

const emit = defineEmits<{
    (event: 'update:modelValue', value: FileSort): void;
}>();

type SortOption = { label: string; sort: FileSort };

const open = ref(false);
const root = ref<HTMLElement | null>(null);

onClickOutside(root, () => {
    open.value = false;
});

// Flatten the key labels into directional entries so the flat list carries both
// directions (like the pagination dropdown) without a separate asc/desc toggle.
const options = computed<SortOption[]>(() => {
    const out: SortOption[] = [];
    const labels = props.labels;

    if (labels.position) {
        out.push({ label: labels.position, sort: { key: 'position', dir: 1 } });
    }

    if (labels.name) {
        out.push({
            label: `${labels.name} (${lang('fields::fields.file.sort.name_asc')})`,
            sort: { key: 'name', dir: 1 },
        });
        out.push({
            label: `${labels.name} (${lang('fields::fields.file.sort.name_desc')})`,
            sort: { key: 'name', dir: -1 },
        });
    }

    if (labels.created_at) {
        out.push({
            label: `${labels.created_at} (${lang('fields::fields.file.sort.created_at_desc')})`,
            sort: { key: 'created_at', dir: -1 },
        });
        out.push({
            label: `${labels.created_at} (${lang('fields::fields.file.sort.created_at_asc')})`,
            sort: { key: 'created_at', dir: 1 },
        });
    }

    return out;
});

const isActive = (sort: FileSort): boolean => props.modelValue?.key === sort.key && props.modelValue?.dir === sort.dir;

const activeLabel = computed<string>(() => {
    const active = options.value.find((option) => isActive(option.sort));

    return active ? active.label : (options.value[0]?.label ?? '');
});

const select = (sort: FileSort) => {
    emit('update:modelValue', sort);
    open.value = false;
};
</script>
